import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, addDays, startOfWeek } from "date-fns";
import { supabase } from "@/lib/supabase";
import { normalizeHHMM, formatTime12 } from "@/components/roster/utils";
import { getAppTimeZone } from "@/lib/dates";
import type { VehicleTypeRow } from "@/lib/vehicleTypes";
import type {
  FleetVehicleType,
  FleetVehicleRow,
  FleetRosterShiftRow,
  FleetJobRow,
  FleetDaySummary,
} from "./types";

export const NZ_WEEK_STARTS_ON = 1; // Monday

export { normalizeHHMM, formatTime12 };

/** taxicaller_data.pu_time comes back as "HH:MM:SS" -- trim to "HH:MM" for reuse with roster time helpers. */
export const trimToHHMM = (raw?: string | null): string => normalizeHHMM(raw?.slice(0, 5));

/** Converts a UTC ISO timestamp (e.g. requested_pickup_at) to its NZ-local "yyyy-MM-dd" calendar date. */
export function nzDateStrFromIso(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: getAppTimeZone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Decimal-hours-of-day (NZ local) for a UTC ISO timestamp, for placing a not-yet-assigned prebooking on the sheet. */
export function nzHourFromIso(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: getAppTimeZone(),
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "00";
  // Some ICU implementations return "24" for midnight with hour12:false.
  return (Number(get("hour")) % 24) + Number(get("minute")) / 60;
}

export const stageVisualState = (stage?: string | null): "cancelled" | "pob" | "delivered" | "other" => {
  const s = (stage || "").toLowerCase();
  if (s === "cancelled" || s === "no show") return "cancelled";
  if (s === "passenger on board") return "pob";
  if (s === "delivered") return "delivered";
  return "other";
};

async function fetchVehiclesByType(vehicleType: FleetVehicleType): Promise<FleetVehicleRow[]> {
  const { data, error } = await supabase
    .from("vehicles")
    .select("vehicle_number, vehicle_type")
    .eq("vehicle_type", vehicleType)
    .order("vehicle_number");
  if (error) throw error;
  return (data as FleetVehicleRow[]) || [];
}

/**
 * Shift type codes that represent actually driving this specific vehicle
 * type (e.g. CAR/STS_CAR/STS_MID for Sedan). Codes like LEAVE/SICK/TRAINING/
 * STANDBY list ALL vehicle types in allowed_vehicle_types (it means "this
 * non-driving activity can be logged against a driver of any type"), not
 * "this is a real Sedan/TMV/Van slot" -- those still carry a vehicle_type on
 * the shift row (e.g. "Sedan - Leave - 435") but must NOT count as capacity.
 * Only codes whose allowed_vehicle_types is a single, specific type qualify.
 */
async function fetchShiftTypeCodesForVehicleType(vehicleType: FleetVehicleType): Promise<string[]> {
  const { data, error } = await supabase.from("roster_shift_types").select("code, allowed_vehicle_types");
  if (error) throw error;
  return (data || [])
    .filter((r: any) => Array.isArray(r.allowed_vehicle_types) && r.allowed_vehicle_types.length === 1 && r.allowed_vehicle_types[0] === vehicleType)
    .map((r: any) => r.code as string);
}

/**
 * Shift-centric, not vehicle-centric: a rostered driver counts as capacity
 * even before a specific vehicle number has been assigned to their shift.
 * Only "published" shifts count -- draft/unconfirmed shifts (including
 * stale drafts left over after a driver goes sick/on leave) are excluded.
 */
async function fetchRosterShiftsByType(shiftTypeCodes: string[], dateFrom: string, dateTo: string): Promise<FleetRosterShiftRow[]> {
  if (shiftTypeCodes.length === 0) return [];
  const { data, error } = await supabase
    .from("roster_shifts")
    .select("id, vehicle_number, driver_name, shift_date, start_hhmm, end_hhmm, status, shift_type_code")
    .in("shift_type_code", shiftTypeCodes)
    .gte("shift_date", dateFrom)
    .lte("shift_date", dateTo)
    .eq("status", "published");
  if (error) throw error;
  return (data as FleetRosterShiftRow[]) || [];
}

const FLEET_JOB_SELECT =
  'id, "Job_ID", vehicle_number, driver_name, pu_date, pu_time, do_date, do_time, "Pick_Up_Location", "Drop_Off_Location", "Stage", job_km, "Passenger_First_Name", "Passenger_Last_Name", estimated_duration_seconds, requested_vehicle_type, requires_wheelchair_vehicle, requested_pickup_at, passenger_count, wheelchair_count, luggage_count, assigned_at, is_asap';

/** dateStr (an NZ calendar date, "yyyy-MM-dd") shifted by whole days, returned as a UTC ISO instant -- used to pad a date-range query bound against a UTC timestamptz column. */
function addUtcDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

const CLOSED_STAGES = new Set(["Delivered", "Cancelled", "No show"]);

// company_name.is.null OR (company_name.not.ilike.<pattern1> AND
// company_name.not.ilike.<pattern2> AND ...) -- a plain
// `.not("company_name", "ilike", pattern)` silently drops every row where
// company_name is NULL too (NULL never satisfies NOT ILIKE in Postgres),
// which is the vast majority of bookings. This keeps those, only excluding
// rows tagged with one of the given company-name patterns.
function excludeCompanyNamePatterns<T>(query: T, patterns: string[]): T {
  const notAnyPattern = patterns.map((p) => `company_name.not.ilike.${p}`).join(",");
  // @ts-expect-error -- PostgrestFilterBuilder's generic chaining doesn't narrow through a helper function
  return query.or(`company_name.is.null,and(${notAnyPattern})`);
}

/**
 * `excludeCrossFleetJobs` keeps an STS-tagged booking off the vehicle's
 * normal vehicle_type tab -- it should only ever show on the STS tab (see
 * fetchCrossFleetCapacity), never both places at once, since STS work is
 * always rostered onto its own STS_* shift, never a normal one.
 *
 * TTC deliberately does NOT get excluded here (see
 * ASSIGNED_JOB_EXCLUDE_PATTERNS) -- confirmed 2026-09-23 by Janelle: TTC
 * vehicles/drivers are never rostered onto a separate TTC_* shift, they stay
 * on their normal roster and she assigns specific bookings to a vehicle
 * ad hoc (in TaxiCaller). Once a TTC job has a real vehicle_number, it needs
 * to show as a normal occupying block on that vehicle's OWN tab (Sedan/SUV)
 * so the vehicle correctly reads as busy there, not as an open gap -- it
 * also still appears on the TTC tab's own overview (fetchJobsByCompanyNamePattern
 * doesn't care about vehicle_number), so it's visible in both places by design.
 *
 * Left false entirely for a cross-fleet tab's own fetch, which deliberately
 * wants a vehicle's full workload regardless of company.
 */
async function fetchJobs(vehicleNumbers: string[], dateFrom: string, dateTo: string, excludeCrossFleetJobs = false): Promise<FleetJobRow[]> {
  if (vehicleNumbers.length === 0) return [];

  // TaxiCaller only populates pu_date/pu_time once a job reaches "Passenger
  // on board" (the Picked_Up field) -- before that (Not started/Waiting/
  // Callout, i.e. assigned but still en route to the pickup) both are null,
  // so the pu_date-ranged query below never finds it and the vehicle looks
  // free even though it's actively out on a job. Runs a second query for
  // exactly that gap -- vehicle assigned, not yet picked up, not closed --
  // and backfills pu_date from assigned_at (NZ local) so every downstream
  // pu_date comparison (buildSlotSheet, weekSummaries) keeps working
  // unchanged. Padded a day each side since assigned_at is UTC and
  // dateFrom/dateTo are NZ calendar dates; computeJobTimes below does the
  // same for the time-of-day.
  let pickedUpQuery = supabase
    .from("taxicaller_data")
    .select(FLEET_JOB_SELECT)
    .in("vehicle_number", vehicleNumbers)
    .gte("pu_date", dateFrom)
    .lte("pu_date", dateTo);
  let preDispatchQuery = supabase
    .from("taxicaller_data")
    .select(FLEET_JOB_SELECT)
    .in("vehicle_number", vehicleNumbers)
    .is("pu_date", null)
    .not("assigned_at", "is", null)
    .gte("assigned_at", addUtcDays(dateFrom, -1))
    .lte("assigned_at", addUtcDays(dateTo, 1));
  if (excludeCrossFleetJobs) {
    pickedUpQuery = excludeCompanyNamePatterns(pickedUpQuery, ASSIGNED_JOB_EXCLUDE_PATTERNS);
    preDispatchQuery = excludeCompanyNamePatterns(preDispatchQuery, ASSIGNED_JOB_EXCLUDE_PATTERNS);
  }

  const [pickedUpResult, preDispatchResult] = await Promise.all([pickedUpQuery, preDispatchQuery]);
  if (pickedUpResult.error) throw pickedUpResult.error;
  if (preDispatchResult.error) throw preDispatchResult.error;

  const backfilled = ((preDispatchResult.data as FleetJobRow[]) || [])
    .filter((job) => !CLOSED_STAGES.has(job.Stage || ""))
    .map((job) => {
      const nzDate = nzDateStrFromIso(job.assigned_at);
      return nzDate ? { ...job, pu_date: nzDate } : job;
    })
    .filter((job) => job.pu_date !== null && job.pu_date >= dateFrom && job.pu_date <= dateTo);

  return [...((pickedUpResult.data as FleetJobRow[]) || []), ...backfilled];
}

/**
 * TaxiCaller's booking-level "Vehicle type"/"Vehicle class" fields (see
 * requested_vehicle_type, migration 0034) use different label text than
 * DMS's own vehicle_type column -- this maps between them so a prebooking
 * that hasn't been assigned a vehicle yet can still be matched against a
 * vehicle type's open slots and, failing that, its overflow section.
 */
const REQUESTED_TYPE_LABEL_BY_VEHICLE_TYPE: Record<string, string> = {
  Sedan: "Sedan",
  SUV: "SUV/Van",
  Wheelchair: "Wheelchair Van",
};

const VEHICLE_TYPE_BY_REQUESTED_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(REQUESTED_TYPE_LABEL_BY_VEHICLE_TYPE).map(([vehicleType, label]) => [label, vehicleType])
);

/** The DMS vehicle_type a not-yet-assigned job needs, derived from what the booking itself requested. */
export function vehicleTypeForRequestedLabel(
  job: Pick<FleetJobRow, "requested_vehicle_type" | "requires_wheelchair_vehicle">
): string | null {
  if (job.requested_vehicle_type) return VEHICLE_TYPE_BY_REQUESTED_LABEL[job.requested_vehicle_type] ?? null;
  if (job.requires_wheelchair_vehicle) return "Wheelchair";
  return null;
}

/**
 * Prebookings TaxiCaller has already created but hasn't assigned a vehicle
 * to yet (see the "New prebooking created" webhook event) -- these have
 * vehicle_number = null, so fetchJobs' vehicle_number-based query never
 * finds them. Fetched once across every type (not per type) since placing
 * one may need to check compatible types too -- see placeUnassignedJobs in
 * slotSheetUtils.ts. Not date-filtered here; that function filters to the
 * viewed day itself. The limit is generous headroom above the ~700-900
 * unassigned forward bookings the TaxiCaller report sync (Forward Jobs
 * report + 15-min refresh) typically keeps on hand -- confirmed a job can
 * otherwise silently drop off the sheet if it's ordered past a too-tight cap.
 */
export async function fetchAllUnassignedForwardJobs(): Promise<FleetJobRow[]> {
  // Cross-fleet-company-tagged jobs (STS, TTC) are excluded here too --
  // still-unassigned bookings for either only ever show via their own
  // dedicated tab's fetch (see fetchCrossFleetCapacity), never through this
  // general vehicle-type placement pool -- unlike an already-ASSIGNED TTC
  // job, which (unlike STS) does still need to show on its vehicle's own
  // tab once assigned; see ASSIGNED_JOB_EXCLUDE_PATTERNS for why that's a
  // different pattern list from this one.
  const { data, error } = await excludeCompanyNamePatterns(
    supabase
      .from("taxicaller_data")
      .select(FLEET_JOB_SELECT)
      .is("vehicle_number", null)
      .not("requested_pickup_at", "is", null),
    UNASSIGNED_POOL_EXCLUDE_PATTERNS
  )
    .order("requested_pickup_at", { ascending: true })
    .limit(3000);
  if (error) throw error;
  return (data as FleetJobRow[]) || [];
}

export const timeToHours = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h + m / 60;
};

/** Rounds a decimal-hours time UP to the next clean mark, e.g. roundUpToStep(11.383, 10/60) -> 11.5 (11:30). */
export function roundUpToStep(h: number, stepH: number): number {
  return Math.ceil(h / stepH - 1e-9) * stepH;
}

const DEFAULT_JOB_DURATION_HOURS = 1 / 3; // 20 min, used when do_time and estimated_duration_seconds are both unknown

// A one-way trip longer than this needs the vehicle to actually drive back
// before it's free again -- the same idea as the lead-time buffer required
// BEFORE a job, just on the other end. No return-route Mapbox call: the
// return leg is estimated by mirroring the outbound duration (roughly the
// same distance back), so a job's total occupied time doubles once it
// crosses the threshold rather than gaining a separate flat top-up.
const RETURN_TRIP_THRESHOLD_HOURS = 20 / 60;

/**
 * A job's start/end in decimal hours, from pu_time/do_time. When drop-off
 * hasn't happened yet (every upcoming/in-progress job), falls back to the
 * TaxiCaller-webhook's Mapbox-derived estimated_duration_seconds rather than
 * a flat 20 minutes, so a genuinely longer trip reserves a bigger buffer.
 * Only falls back to the flat default when no real estimate exists either.
 *
 * startH itself falls back through two signals when pu_time isn't set yet:
 * requested_pickup_at (NZ local time-of-day) for a not-yet-assigned
 * prebooking, which has no time signal at all until a driver picks it up;
 * then assigned_at for a job that IS assigned to a vehicle but hasn't been
 * picked up yet (Not started/Waiting/Callout) -- pu_date/pu_time backfilled
 * onto these rows in fetchJobs, but pu_time is deliberately left null so
 * this fallback chain (rather than a fabricated pu_time) decides the hour.
 */
export function computeJobTimes(job: FleetJobRow): { startH: number; endH: number } {
  const startH =
    (job.pu_time ? timeToHours(trimToHHMM(job.pu_time)) : null) ??
    nzHourFromIso(job.requested_pickup_at) ??
    nzHourFromIso(job.assigned_at) ??
    0;
  let oneWayH: number;
  if (job.do_time && job.do_date === job.pu_date) {
    oneWayH = timeToHours(trimToHHMM(job.do_time)) - startH;
    if (oneWayH <= 0) oneWayH = DEFAULT_JOB_DURATION_HOURS;
  } else if (job.estimated_duration_seconds && job.estimated_duration_seconds > 0) {
    oneWayH = job.estimated_duration_seconds / 3600;
  } else {
    oneWayH = DEFAULT_JOB_DURATION_HOURS;
  }
  const totalH = oneWayH > RETURN_TRIP_THRESHOLD_HOURS + 1e-9 ? oneWayH * 2 : oneWayH;
  return { startH, endH: startH + totalH };
}

/**
 * The STS and TTC tabs aren't real vehicle_types -- each is a cross-cutting
 * view of "work dispatched under this company", spanning whichever vehicles
 * happen to be rostered on that company's own shift-type prefix that day
 * (any of DMS's real vehicle types). Kept out of the vehicle_types table
 * entirely (that table drives real dropdowns -- VehicleEditModal, roster
 * position types, the compatibility matrix -- none of which should ever
 * offer "STS"/"TTC" as an actual vehicle type) and constructed client-side
 * instead. See fetchCrossFleetCapacity.
 *
 * TTC Services ("profiled" vehicles/drivers only -- confirmed 2026-09-22 by
 * Janelle) reuses the exact same mechanism STS already established: a
 * vehicle only shows on the TTC tab if a driver is rostered on a TTC_*
 * shift that day (added via Settings > Roster Positions, the same
 * self-serve screen STS_CAR/STS_MID/STS_LATE were originally created
 * through -- no new plumbing needed for "only some vehicles get this work").
 */
export const STS_TYPE_KEY = "STS";
export const TTC_TYPE_KEY = "TTC";

export const STS_VEHICLE_TYPE: VehicleTypeRow = {
  id: "sts-virtual",
  vehicle_type: STS_TYPE_KEY,
  icon_style: "car",
  colour_hex: "#0EA5E9",
  overlay_asset_key: null,
  slot_length_minutes: 15,
  load_buffer_minutes: 0,
  unload_buffer_minutes: 0,
  return_buffer_required: false,
  return_buffer_trigger: "drive_time",
  return_buffer_minutes: 0,
  drive_time_threshold_minutes: 0,
  min_spacing_minutes: 0,
  max_jobs_per_period: null,
  is_active: true,
  sort_order: 9999,
};

export const TTC_VEHICLE_TYPE: VehicleTypeRow = {
  id: "ttc-virtual",
  vehicle_type: TTC_TYPE_KEY,
  icon_style: "car",
  colour_hex: "#A855F7",
  overlay_asset_key: null,
  slot_length_minutes: 15,
  load_buffer_minutes: 0,
  unload_buffer_minutes: 0,
  return_buffer_required: false,
  return_buffer_trigger: "drive_time",
  return_buffer_minutes: 0,
  drive_time_threshold_minutes: 0,
  min_spacing_minutes: 0,
  max_jobs_per_period: null,
  is_active: true,
  sort_order: 10000,
};

// Confirmed via real data (migration 0040): TaxiCaller's company_name for
// STS bookings comes through as "Specialty Transport Services". Matched
// loosely (not exact-equality) since dispatch/TaxiCaller-side spelling can
// drift and this is the only signal available for a booking that hasn't
// been dispatched to an STS-shift vehicle yet.
const STS_COMPANY_NAME_PATTERN = "%specialty%";

// TaxiCaller's company_name for TTC Services bookings -- confirmed by
// Janelle 2026-09-22 (renamed from "Taxis Gold & Black Feilding" in
// TaxiCaller the day before; see tenant_taxicaller_companies). Same loose
// ilike match as STS, for the same reason.
const TTC_COMPANY_NAME_PATTERN = "%ttc%";

// Used to keep a still-UNASSIGNED cross-fleet booking out of the general
// "not yet assigned" placement pool every normal vehicle-type tab shares
// (see fetchAllUnassignedForwardJobs) -- both STS and TTC stay off it,
// since neither should show as a tentative placement on an ordinary Sedan/
// SUV/Wheelchair tab before Janelle has actually decided which vehicle
// takes it.
const UNASSIGNED_POOL_EXCLUDE_PATTERNS = [STS_COMPANY_NAME_PATTERN, TTC_COMPANY_NAME_PATTERN];

// Used to keep an already-ASSIGNED cross-fleet job off its own vehicle's
// normal tab (see fetchJobs' excludeCrossFleetJobs). STS only -- TTC is
// deliberately absent, since a TTC vehicle stays on its normal roster and
// an assigned TTC job must still block that vehicle's own Sedan/SUV lane
// (confirmed 2026-09-23 by Janelle: "it should still show that vehicle as
// unavailable" once she's assigned it, even though it's TTC work, not TGB).
const ASSIGNED_JOB_EXCLUDE_PATTERNS = [STS_COMPANY_NAME_PATTERN];

/** roster_shift_types codes with the given prefix (e.g. "STS_"/"TTC_") -- confirmed prefix convention, not a broader flag column (STS's own "requires_sts" flag, for example, also covers the unrelated DMD loan-shift type). */
async function fetchShiftTypeCodesByPrefix(prefix: string): Promise<string[]> {
  const { data, error } = await supabase.from("roster_shift_types").select("code");
  if (error) throw error;
  return (data || []).map((r: { code: string }) => r.code).filter((code) => code.startsWith(prefix));
}

/**
 * Jobs tagged with a cross-fleet company name (STS, TTC), regardless of
 * whether TaxiCaller has dispatched them to a vehicle yet -- mirrors
 * fetchJobs' three-bucket pu_date/assigned_at/requested_pickup_at backfill
 * chain, but keyed on company_name instead of vehicle_number, and
 * additionally covers jobs with no vehicle assigned at all (a cross-fleet
 * booking has no "type" to place it via placeUnassignedJobs, so it's
 * pu_date-backfilled here instead and left for buildSlotSheet's own
 * overflow bucket to pick up).
 */
async function fetchJobsByCompanyNamePattern(companyNamePattern: string, dateFrom: string, dateTo: string): Promise<FleetJobRow[]> {
  const [pickedUpResult, preDispatchResult, unassignedResult] = await Promise.all([
    supabase
      .from("taxicaller_data")
      .select(FLEET_JOB_SELECT)
      .ilike("company_name", companyNamePattern)
      .gte("pu_date", dateFrom)
      .lte("pu_date", dateTo),
    supabase
      .from("taxicaller_data")
      .select(FLEET_JOB_SELECT)
      .ilike("company_name", companyNamePattern)
      .is("pu_date", null)
      .not("assigned_at", "is", null)
      .gte("assigned_at", addUtcDays(dateFrom, -1))
      .lte("assigned_at", addUtcDays(dateTo, 1)),
    supabase
      .from("taxicaller_data")
      .select(FLEET_JOB_SELECT)
      .ilike("company_name", companyNamePattern)
      .is("pu_date", null)
      .is("assigned_at", null)
      .not("requested_pickup_at", "is", null)
      .gte("requested_pickup_at", addUtcDays(dateFrom, -1))
      .lte("requested_pickup_at", addUtcDays(dateTo, 1)),
  ]);
  if (pickedUpResult.error) throw pickedUpResult.error;
  if (preDispatchResult.error) throw preDispatchResult.error;
  if (unassignedResult.error) throw unassignedResult.error;

  const backfillPuDate = (rows: FleetJobRow[] | null, source: "assigned_at" | "requested_pickup_at") =>
    (rows || [])
      .filter((job) => !CLOSED_STAGES.has(job.Stage || ""))
      .map((job) => {
        const nzDate = nzDateStrFromIso(job[source]);
        return nzDate ? { ...job, pu_date: nzDate } : job;
      })
      .filter((job) => job.pu_date !== null && job.pu_date >= dateFrom && job.pu_date <= dateTo);

  return [
    ...((pickedUpResult.data as FleetJobRow[]) || []),
    ...backfillPuDate(preDispatchResult.data as FleetJobRow[], "assigned_at"),
    ...backfillPuDate(unassignedResult.data as FleetJobRow[], "requested_pickup_at"),
  ];
}

/**
 * Lanes come from real rostered shifts on the given prefix (same
 * shift-centric model as every other type); jobs are the union of whatever's
 * assigned to those shifts' vehicles that day (same as fetchJobs, so a
 * driver's full workload shows, not just their cross-fleet-labelled jobs --
 * parity with how a Sedan tab shows all of a Sedan vehicle's jobs) plus
 * anything TaxiCaller has tagged with the matching company name, dispatched
 * or not. Deduped by row id since a job can satisfy both criteria at once.
 *
 * Split into `jobs` (has a real vehicle_number -- matched to a shift lane by
 * buildSlotSheet the normal way, or its own overflow if that vehicle wasn't
 * on a matching shift that day) and `unassignedCrossFleetJobs` (no vehicle
 * yet -- this work doesn't care which vehicle takes it, so these get a
 * dedicated gap-seeking placement pass, see placeUnassignedCrossFleetJobs,
 * instead of being matched by vehicle_number like every other job and
 * always missing).
 */
async function fetchCrossFleetCapacity(
  shiftTypePrefix: string,
  companyNamePattern: string,
  dateFrom: string,
  dateTo: string
): Promise<{ vehicles: FleetVehicleRow[]; shifts: FleetRosterShiftRow[]; jobs: FleetJobRow[]; unassignedCrossFleetJobs: FleetJobRow[] }> {
  const codes = await fetchShiftTypeCodesByPrefix(shiftTypePrefix);
  const shifts = await fetchRosterShiftsByType(codes, dateFrom, dateTo);
  const vehicleNumbers = Array.from(
    new Set(shifts.map((s) => s.vehicle_number).filter((v): v is string => !!v))
  );
  const [vehicleJobs, companyJobs] = await Promise.all([
    fetchJobs(vehicleNumbers, dateFrom, dateTo),
    fetchJobsByCompanyNamePattern(companyNamePattern, dateFrom, dateTo),
  ]);
  const jobsById = new Map<string, FleetJobRow>();
  [...vehicleJobs, ...companyJobs].forEach((j) => jobsById.set(j.id, j));

  const jobs: FleetJobRow[] = [];
  const unassignedCrossFleetJobs: FleetJobRow[] = [];
  for (const j of jobsById.values()) {
    (j.vehicle_number ? jobs : unassignedCrossFleetJobs).push(j);
  }
  return { vehicles: [], shifts, jobs, unassignedCrossFleetJobs };
}

async function fetchFleetCapacityForType(
  vehicleType: FleetVehicleType,
  dateFrom: string,
  dateTo: string
): Promise<{ vehicles: FleetVehicleRow[]; shifts: FleetRosterShiftRow[]; jobs: FleetJobRow[]; unassignedCrossFleetJobs?: FleetJobRow[] }> {
  if (vehicleType === STS_TYPE_KEY) {
    return fetchCrossFleetCapacity("STS_", STS_COMPANY_NAME_PATTERN, dateFrom, dateTo);
  }
  if (vehicleType === TTC_TYPE_KEY) {
    return fetchCrossFleetCapacity("TTC_", TTC_COMPANY_NAME_PATTERN, dateFrom, dateTo);
  }
  const v = await fetchVehiclesByType(vehicleType);
  const vehicleNumbers = v.map((x) => x.vehicle_number);
  const shiftTypeCodes = await fetchShiftTypeCodesForVehicleType(vehicleType);
  const [s, j] = await Promise.all([
    fetchRosterShiftsByType(shiftTypeCodes, dateFrom, dateTo),
    fetchJobs(vehicleNumbers, dateFrom, dateTo, true),
  ]);
  return { vehicles: v, shifts: s, jobs: j };
}

export interface FleetCapacityByType {
  shifts: FleetRosterShiftRow[];
  jobs: FleetJobRow[];
  /** Cross-fleet-company jobs (STS, TTC) with no vehicle assigned yet -- only ever populated for a cross-fleet pseudo-type. See fetchCrossFleetCapacity. */
  unassignedCrossFleetJobs?: FleetJobRow[];
}

const FLEET_CAPACITY_POLL_MS = 25_000;

/** Fetches one day's shifts+jobs for every vehicle type at once, for the combined slot sheet. */
export function useFleetCapacityDataForAllTypes(selectedDate: Date, vehicleTypes: { vehicle_type: string }[]) {
  const [dataByType, setDataByType] = useState<Record<string, FleetCapacityByType>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const typeKey = vehicleTypes.map((t) => t.vehicle_type).join(",");

  // "Latest request wins", not a shared cancelled flag -- a plain boolean
  // ref reset to false by a NEW effect run (on date/type change) before an
  // OLDER in-flight request's promise has resolved would let that stale
  // response slip through and overwrite the newer one, since the reset
  // happens synchronously ahead of the older request's own `await`
  // finishing. Comparing against the most recently *started* request's own
  // key instead is immune to that ordering, however many requests overlap.
  const latestRequestKeyRef = useRef<string>("");

  const load = useCallback(
    async (showLoading: boolean) => {
      const requestKey = `${dateStr}|${typeKey}`;
      latestRequestKeyRef.current = requestKey;
      if (vehicleTypes.length === 0) {
        setDataByType({});
        setLoading(false);
        return;
      }
      if (showLoading) setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          vehicleTypes.map((t) => fetchFleetCapacityForType(t.vehicle_type, dateStr, dateStr))
        );
        if (latestRequestKeyRef.current !== requestKey) return;
        const next: Record<string, FleetCapacityByType> = {};
        vehicleTypes.forEach((t, i) => {
          next[t.vehicle_type] = { shifts: results[i].shifts, jobs: results[i].jobs, unassignedCrossFleetJobs: results[i].unassignedCrossFleetJobs };
        });
        setDataByType(next);
      } catch (e: any) {
        if (latestRequestKeyRef.current === requestKey) setError(e?.message || "Failed to load fleet capacity data");
      } finally {
        if (showLoading && latestRequestKeyRef.current === requestKey) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateStr, typeKey]
  );

  useEffect(() => {
    load(true);
    // Silent background refetch so newly-assigned/updated TaxiCaller jobs show
    // up without the dispatcher having to change date or reload the page --
    // doesn't touch `loading`, so it never re-shows the "Loading..." state.
    const id = setInterval(() => load(false), FLEET_CAPACITY_POLL_MS);
    return () => {
      clearInterval(id);
    };
  }, [load]);

  // Exposed so a manual "refresh bookings" action (which re-pulls TaxiCaller's
  // own report, not this table) can pull the freshly-written rows back in
  // right away instead of waiting for the next 25s poll.
  const reload = useCallback(() => load(true), [load]);

  return { dataByType, loading, error, reload };
}

export function useFleetCapacityData(selectedDate: Date, viewMode: "day" | "week", vehicleType: FleetVehicleType) {
  const [vehicles, setVehicles] = useState<FleetVehicleRow[]>([]);
  const [shifts, setShifts] = useState<FleetRosterShiftRow[]>([]);
  const [jobs, setJobs] = useState<FleetJobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rangeStart = useMemo(
    () => (viewMode === "week" ? startOfWeek(selectedDate, { weekStartsOn: NZ_WEEK_STARTS_ON }) : selectedDate),
    [selectedDate, viewMode]
  );
  const rangeEnd = useMemo(() => (viewMode === "week" ? addDays(rangeStart, 6) : selectedDate), [rangeStart, selectedDate, viewMode]);
  const dateFrom = format(rangeStart, "yyyy-MM-dd");
  const dateTo = format(rangeEnd, "yyyy-MM-dd");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { vehicles: v, shifts: s, jobs: j } = await fetchFleetCapacityForType(vehicleType, dateFrom, dateTo);
        if (cancelled) return;
        setVehicles(v);
        setShifts(s);
        setJobs(j);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load fleet capacity data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleType, dateFrom, dateTo]);

  const weekSummaries: FleetDaySummary[] = useMemo(() => {
    if (viewMode !== "week") return [];
    const days: FleetDaySummary[] = [];
    for (let i = 0; i < 7; i++) {
      const d = format(addDays(rangeStart, i), "yyyy-MM-dd");
      // Count rostered capacity: distinct vehicle_number when assigned, else one per unassigned shift.
      const dayShifts = shifts.filter((s) => s.shift_date === d);
      const vehiclesRostered = new Set(dayShifts.map((s) => s.vehicle_number || s.id)).size;
      const jobCount = jobs.filter((j) => j.pu_date === d).length;
      days.push({ date: d, vehiclesRostered, jobCount });
    }
    return days;
  }, [viewMode, rangeStart, shifts, jobs]);

  return { vehicles, shifts, jobs, weekSummaries, loading, error, dateFrom, dateTo };
}
